import Vue from 'vue';
import moment from 'moment';
import { prepareFormQuestions } from "@/services/forms";
import {
  CaseState,
  CaseFilter,
  CasePatientFilter,
  CaseDateFilter,
  CaseRecord,
  CaseLogEntry,
  CaseTaskFilter,
  Tab,
  CaseTaskRecord,
  User,
} from "./types";

const getDefaultCaseFilter = (): CaseFilter => {
  return {
    entityId: "",
    campaignId: "",
    id: "",
    patient: {
      id: "",
      firstName: "",
      lastName: "",
      insurerMemberId: "",
      EMR_ID: "",
      birthdate: {
        from: null,
        to: null,
      },
      city: "",
      gender: "",
    },
    referralDate: {
      from: null,
      to: null,
    },
    typeList: [],
    riskLevelList: [],
    activityList: [],
    statusList: [],
    ownerUserList: [],
    created: {
      from: null,
      to: null,
    },
    updated: {
      from: null,
      to: null,
    },
    orderBy: "ID",
    pageNumber: 1,
    rowsPerPage: 25
  };
};

const getDefaultCaseTaskFilter = (): CaseTaskFilter => {
  return {
    entityId: "",
    caseId: "",
    id: "",
    patient: {
      id: "",
      firstName: "",
      lastName: "",
      insurerMemberId: "",
      EMR_ID: "",
      birthdate: {
        from: null,
        to: null,
      },
      city: "",
      gender: "",
    },
    caseTypeList: [],
    statusList: [],
    priorityList: [],
    dueDate: {
      from: null,
      to: null,
    },
    created: {
      from: null,
      to: null,
    },
    updated: {
      from: null,
      to: null,
    },
    ownerUserList: [],
    orderBy: "ID",
    pageNumber: 1,
    rowsPerPage: 25
  };
};

const dashboardTab: Tab = {
  id: "DASHBOARD",
  name: "Dashboard",
  component: "Dashboard",
  props: {
    tabId: "DASHB"
  },
  showRemove: false,
}

const taskListTab: Tab = {
  id: "TASK_LIST",
  name: "Task List",
  component: "Tasks",
  props: {
    tabId: "TASK_LIST"
  },
  showRemove: false,
}

const taskCalendarTab: Tab = {
  id: "TASK_CALENDAR",
  name: "Task Calendar",
  component: "TaskCalendar",
  props: {
    tabId: "TASK_CALENDAR"
  },
  showRemove: false,
}

const getDefaultState = () => {
  const state: CaseState = {
    tabs: {
      active: dashboardTab,
      list: [
        dashboardTab,
        taskListTab,
        taskCalendarTab
      ],
    },
    dashboard: {
      filter: getDefaultCaseFilter(),
      pageRows: [],
      totalRows: 0
    },
    tasks: {
      filter: getDefaultCaseTaskFilter(),
      pageRows: [],
      totalRows: 0
    },
    types: [],
    triggers: [],
    refSources: [],
    status: [],
    closeReasons: [],
    activity: [],
    riskLevels: [],
    logTypes: [],
    forms: [],
    clinicalIndicators: [],
    users: [],
    carePlan: {
      types: [],
      problems: [],
      problemStatus: [],
      interventions: [],
      interventionStatus: [],
      goals: [],
      goalStatus: [],
      tasks: [],
      taskStatus: [],
      taskPriority: []
    }
  };
  return state;
};

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    SET_ACTIVE_TAB(state: CaseState, tab: Tab) {
      state.tabs.active = tab;
    },
    ADD_NEW_TAB(state: CaseState, tab: Tab) {
      state.tabs.list.push(tab);
      state.tabs.active = tab;
    },
    SET_TAB_CASE_RECORD(state: CaseState, { tabId, record }) {
      const tab = state.tabs.list.find(t => t.id === tabId);
      if (tab) tab.props.caseRecord = record;
    },
    SET_TAB_CASE_FORM_QUESTIONS(state: CaseState, { tabId, questions }) {
      const tab = state.tabs.list.find(t => t.id === tabId);
      if (tab) tab.props.formQuestions = questions;
    },
    SET_TAB_NAME(state: CaseState, { id, name }) {
      const tab = state.tabs.list.find(t => t.id === id);
      if (tab) tab.name = name;
    },
    REMOVE_TAB(state: CaseState, id: string) {
      const { list } = state.tabs;
      // const isActive = state.tabs.active.id === id;
      // const isLastTab = list[list.length - 1].id === id;
      const index = list.findIndex((t) => t.id === id);
      list.splice(index, 1);
      state.tabs.active = list[0];
      // if (isActive) {
      //   if (isLastTab) {
      //     state.tabs.active = list[list.length - 1];
      //   } else {
      //     state.tabs.active = list[0];
      //   }
      // }
    },
    SET_TYPES(state: CaseState, types: Array<any>) {
      state.types = types;
    },
    SET_TRIGGERS(state: CaseState, triggers: Array<any>) {
      state.triggers = triggers;
    },
    SET_REF_SOURCES(state: CaseState, sources: Array<any>) {
      state.refSources = sources;
    },
    SET_ACTIVITY(state: CaseState, activity: Array<any>) {
      state.activity = activity;
    },
    SET_RISK_LEVELS(state: CaseState, riskLevels: Array<any>) {
      state.riskLevels = riskLevels;
    },
    SET_STATUS(state: CaseState, status: Array<any>) {
      state.status = status;
    },
    SET_CLOSE_REASONS(state: CaseState, reasons: Array<any>) {
      state.closeReasons = reasons;
    },
    SET_FORMS(state: CaseState, forms: Array<any>) {
      state.forms = forms;
    },
    SET_CLINICAL_INDICATORS(state: CaseState, indicators: Array<any>) {
      state.clinicalIndicators = indicators;
    },
    SET_USERS(state: CaseState, users: Array<any>) {
      state.users = users;
    },
    SET_CARE_PLAN_TYPES(state: CaseState, types: Array<any>) {
      state.carePlan.types = types;
    },
    SET_CARE_PLAN_PROBLEMS(state: CaseState, problems: Array<any>) {
      state.carePlan.problems = problems;
    },
    SET_CARE_PLAN_PROBLEM_STATUS(state: CaseState, status: Array<any>) {
      state.carePlan.problemStatus = status;
    },
    SET_CARE_PLAN_INTERVENTIONS(state: CaseState, interventions: Array<any>) {
      state.carePlan.interventions = interventions;
    },
    SET_CARE_PLAN_INTERVENTION_STATUS(state: CaseState, status: Array<any>) {
      state.carePlan.interventionStatus = status;
    },
    SET_CARE_PLAN_GOALS(state: CaseState, goals: Array<any>) {
      state.carePlan.goals = goals;
    },
    SET_CARE_PLAN_GOAL_STATUS(state: CaseState, status: Array<any>) {
      state.carePlan.goalStatus = status;
    },
    SET_CARE_PLAN_TASKS(state: CaseState, tasks: Array<any>) {
      state.carePlan.tasks = tasks.map(task => {
        task.statusId = "OPN";
        task.priorityId = "NM";
        task.dueDate = null;
        task.key = null;
        return task;
      });
    },
    SET_CARE_PLAN_TASK_STATUS(state: CaseState, status: Array<any>) {
      state.carePlan.taskStatus = status;
    },
    SET_CARE_PLAN_TASK_PRIORITY(state: CaseState, priority: Array<any>) {
      state.carePlan.taskPriority = priority;
    },
    SET_LOG_TYPES(state: CaseState, logTypes: Array<any>) {
      state.logTypes = logTypes;
    },
    SET_CASE_PAGE(state: CaseState, page: any) {
      state.dashboard.pageRows = page.pageRows.map((c) => {
        c.checked = false;
        return c;
      });
      state.dashboard.totalRows = page.totalRows;
    },
    // CASE FILTERS
    SET_FILTER(state: CaseState, filter: CaseFilter) {
      state.dashboard.filter = filter;
    },
    SET_ENTITY_FILTER(state: CaseState, id: string) {
      state.dashboard.filter.entityId = id;
    },
    SET_CAMPAIGN_FILTER(state: CaseState, id: string) {
      state.dashboard.filter.campaignId = id;
    },
    SET_ID_FILTER(state: CaseState, id: string) {
      state.dashboard.filter.id = id;
    },
    SET_PATIENT_FILTER(state: CaseState, filter: CasePatientFilter) {
      state.dashboard.filter.patient = filter;
    },
    SET_TYPE_FILTER(state: CaseState, filter: number[]) {
      state.dashboard.filter.typeList = filter;
    },
    SET_REFERRAL_DATE_FILTER(state: CaseState, filter: CaseDateFilter) {
      state.dashboard.filter.referralDate = filter;
    },
    SET_RISK_LEVEL_FILTER(state: CaseState, filter: string[]) {
      state.dashboard.filter.riskLevelList = filter;
    },
    SET_ACTIVITY_FILTER(state: CaseState, filter: number[]) {
      state.dashboard.filter.activityList = filter;
    },
    SET_STATUS_FILTER(state: CaseState, filter: string[]) {
      state.dashboard.filter.statusList = filter;
    },
    SET_OWNER_USER_FILTER(state: CaseState, filter: number[]) {
      state.dashboard.filter.ownerUserList = filter;
    },
    SET_CREATED_FILTER(state: CaseState, filter: CaseDateFilter) {
      state.dashboard.filter.created = filter;
    },
    SET_UPDATED_FILTER(state: CaseState, filter: CaseDateFilter) {
      state.dashboard.filter.updated = filter;
    },
    SET_ORDER_BY_FILTER(state: CaseState, field: string) {
      state.dashboard.filter.orderBy = field;
      state.dashboard.filter.pageNumber = 1;
    },
    SET_PAGE_NUMBER(state: CaseState, number: number) {
      state.dashboard.filter.pageNumber = number;
    },
    SET_ROWS_PER_PAGE(state: CaseState, number: number) {
      state.dashboard.filter.rowsPerPage = number;
    },
    RESET_FILTER(state: CaseState, entityId: string) {
      state.dashboard.filter = getDefaultCaseFilter();
      if (entityId) {
        state.dashboard.filter.entityId = entityId;
      }
    },
    // TASK FILTERS
    SET_CASE_TASKS_FILTER(state: CaseState, filter: CaseTaskFilter) {
      state.tasks.filter = filter;
    },
    SET_CASE_TASKS_CASE_ID_FILTER(state: CaseState, id: string) {
      state.tasks.filter.caseId = id;
    },
    SET_CASE_TASKS_ID_FILTER(state: CaseState, id: string) {
      state.tasks.filter.id = id;
    },
    SET_CASE_TASKS_PATIENT_FILTER(state: CaseState, filter: CasePatientFilter) {
      state.tasks.filter.patient = filter;
    },
    SET_CASE_TASKS_CASE_TYPE_FILTER(state: CaseState, filter: number[]) {
      state.tasks.filter.caseTypeList = filter;
    },
    SET_CASE_TASKS_STATUS_FILTER(state: CaseState, filter: string[]) {
      state.tasks.filter.statusList = filter;
    },
    SET_CASE_TASKS_PRIORITY_FILTER(state: CaseState, filter: string[]) {
      state.tasks.filter.priorityList = filter;
    },
    SET_CASE_TASKS_OWNER_FILTER(state: CaseState, filter: number[]) {
      state.tasks.filter.ownerUserList = filter;
    },
    SET_CASE_TASKS_DUE_DATE_FILTER(state: CaseState, filter: CaseDateFilter) {
      state.tasks.filter.dueDate = filter;
    },
    SET_CASE_TASKS_CREATED_FILTER(state: CaseState, filter: CaseDateFilter) {
      state.tasks.filter.created = filter;
    },
    SET_CASE_TASKS_PAGE(state: CaseState, page: any) {
      state.tasks.pageRows = page.pageRows.map((c) => {
        c.checked = false;
        return c;
      });
      state.tasks.totalRows = page.totalRows;
    },
    SET_CASE_TASKS_ORDER_BY_FILTER(state: CaseState, field: string) {
      state.tasks.filter.orderBy = field;
      state.tasks.filter.pageNumber = 1;
    },
    SET_CASE_TASKS_PAGE_NUMBER(state: CaseState, number: number) {
      state.tasks.filter.pageNumber = number;
    },
    SET_CASE_TASKS_ROWS_PER_PAGE(state: CaseState, number: number) {
      state.tasks.filter.rowsPerPage = number;
    },
    RESET_CASE_TASKS_FILTER(state: CaseState, entityId: string) {
      state.tasks.filter = getDefaultCaseTaskFilter();
      if (entityId) {
        state.tasks.filter.entityId = entityId;
      }
    },
    //
    RESET_STATE(state: CaseState) {
      Object.assign(state, getDefaultState());
    },
  },
  actions: {
    async getList({ state, commit }) {
      // SET FORM DATA
      const filter = state.dashboard.filter as CaseFilter;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      params.append("campaignId", filter.campaignId);
      params.append("id", filter.id);
      params.append("patientId", filter.patient.id);
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append("patientGender", filter.patient.gender);
      params.append("patientCity", filter.patient.city);
      params.append(
        "patientBirthdateFrom",
        moment(filter.patient.birthdate.from).format("YYYY-MM-DD")
      );
      params.append(
        "patientBirthdateTo",
        moment(filter.patient.birthdate.to).format("YYYY-MM-DD")
      );
      params.append(
        "referralDateFrom",
        moment(filter.referralDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referralDateTo",
        moment(filter.referralDate.to).format("YYYY-MM-DD")
      );
      if (filter.typeList && filter.typeList.length) {
        params.append("typeList", filter.typeList.join());
      }
      if (filter.riskLevelList && filter.riskLevelList.length) {
        params.append(
          "riskLevelList",
          filter.riskLevelList
            .map((level) => {
              return "'" + level + "'";
            })
            .join()
        );
      }
      if (filter.activityList && filter.activityList.length) {
        params.append("activityList", filter.activityList.join());
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append("ownerUserList", filter.ownerUserList.join());
      }
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateFrom",
        moment(filter.updated.from).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateTo",
        moment(filter.updated.to).format("YYYY-MM-DD")
      );
      params.append("orderBy", filter.orderBy);
      params.append("pageNumber", String(filter.pageNumber));
      params.append("rowsPerPage", String(filter.rowsPerPage));
      // GET CASE LIST ASYNC
      const { data } = await Vue.axios.post("cases/list", params);
      //
      commit("SET_CASE_PAGE", data);
    },
    async getListCSV({ state }) {
      // SET FORM DATA
      const filter = state.dashboard.filter as CaseFilter;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      params.append("campaignId", filter.campaignId);
      params.append("id", filter.id);
      params.append("patientId", filter.patient.id);
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append("patientGender", filter.patient.gender);
      params.append("patientCity", filter.patient.city);
      params.append(
        "patientBirthdateFrom",
        moment(filter.patient.birthdate.from).format("YYYY-MM-DD")
      );
      params.append(
        "patientBirthdateTo",
        moment(filter.patient.birthdate.to).format("YYYY-MM-DD")
      );
      params.append(
        "referralDateFrom",
        moment(filter.referralDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referralDateTo",
        moment(filter.referralDate.to).format("YYYY-MM-DD")
      );
      if (filter.typeList && filter.typeList.length) {
        params.append("typeList", filter.typeList.join());
      }
      if (filter.riskLevelList && filter.riskLevelList.length) {
        params.append(
          "riskLevelList",
          filter.riskLevelList
            .map((level) => {
              return "'" + level + "'";
            })
            .join()
        );
      }
      if (filter.activityList && filter.activityList.length) {
        params.append("activityList", filter.activityList.join());
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append("ownerUserList", filter.ownerUserList.join());
      }
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateFrom",
        moment(filter.updated.from).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateTo",
        moment(filter.updated.to).format("YYYY-MM-DD")
      );
      params.append("orderBy", filter.orderBy);
      // GET CASE LIST CSV ASYNC
      const { data } = await Vue.axios.post("cases/list/csv", params, {
        responseType: "blob",
      });
      //
      return data;
    },
    async getListExcel({ state }) {
      // SET FORM DATA
      const filter = state.dashboard.filter as CaseFilter;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      params.append("campaignId", filter.campaignId);
      params.append("id", filter.id);
      params.append("patientId", filter.patient.id);
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append("patientGender", filter.patient.gender);
      params.append("patientCity", filter.patient.city);
      params.append(
        "patientBirthdateFrom",
        moment(filter.patient.birthdate.from).format("YYYY-MM-DD")
      );
      params.append(
        "patientBirthdateTo",
        moment(filter.patient.birthdate.to).format("YYYY-MM-DD")
      );
      params.append(
        "referralDateFrom",
        moment(filter.referralDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referralDateTo",
        moment(filter.referralDate.to).format("YYYY-MM-DD")
      );
      if (filter.typeList && filter.typeList.length) {
        params.append("typeList", filter.typeList.join());
      }
      if (filter.riskLevelList && filter.riskLevelList.length) {
        params.append(
          "riskLevelList",
          filter.riskLevelList
            .map((level) => {
              return "'" + level + "'";
            })
            .join()
        );
      }
      if (filter.activityList && filter.activityList.length) {
        params.append("activityList", filter.activityList.join());
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append("ownerUserList", filter.ownerUserList.join());
      }
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateFrom",
        moment(filter.updated.from).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateTo",
        moment(filter.updated.to).format("YYYY-MM-DD")
      );
      params.append("orderBy", filter.orderBy);
      // GET CASE LIST EXCEL ASYNC
      const { data } = await Vue.axios.post("cases/list/excel", params, {
        responseType: "blob",
      });
      //
      return data;
    },
    async getTasks({ state, commit }) {
      // SET FORM DATA
      const filter: CaseTaskFilter = state.tasks.filter;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      params.append("caseId", filter.caseId);
      params.append("id", filter.id);
      params.append("patientId", filter.patient.id);
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append("patientGender", filter.patient.gender);
      params.append("patientCity", filter.patient.city);
      params.append(
        "patientBirthdateFrom",
        moment(filter.patient.birthdate.from).format("YYYY-MM-DD")
      );
      params.append(
        "patientBirthdateTo",
        moment(filter.patient.birthdate.to).format("YYYY-MM-DD")
      );
      if (filter.caseTypeList && filter.caseTypeList.length) {
        params.append("caseTypeList", filter.caseTypeList.join());
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      if (filter.priorityList && filter.priorityList.length) {
        params.append(
          "priorityList",
          filter.priorityList
            .map((priority) => {
              return "'" + priority + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append("ownerUserList", filter.ownerUserList.join());
      }
      params.append(
        "dueDateFrom",
        moment(filter.dueDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "dueDateTo",
        moment(filter.dueDate.to).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append("orderBy", filter.orderBy);
      params.append("pageNumber", String(filter.pageNumber));
      params.append("rowsPerPage", String(filter.rowsPerPage));
      // GET TASK LIST ASYNC
      const { data } = await Vue.axios.post("cases/task/list", params);
      //
      commit("SET_CASE_TASKS_PAGE", data);
    },
    async getTasksCSV({ state, commit }) {
      // SET FORM DATA
      const filter: CaseTaskFilter = state.tasks.filter;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      params.append("caseId", filter.caseId);
      params.append("id", filter.id);
      params.append("patientId", filter.patient.id);
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append("patientGender", filter.patient.gender);
      params.append("patientCity", filter.patient.city);
      params.append(
        "patientBirthdateFrom",
        moment(filter.patient.birthdate.from).format("YYYY-MM-DD")
      );
      params.append(
        "patientBirthdateTo",
        moment(filter.patient.birthdate.to).format("YYYY-MM-DD")
      );
      if (filter.caseTypeList && filter.caseTypeList.length) {
        params.append("caseTypeList", filter.caseTypeList.join());
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      if (filter.priorityList && filter.priorityList.length) {
        params.append(
          "priorityList",
          filter.priorityList
            .map((priority) => {
              return "'" + priority + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append("ownerUserList", filter.ownerUserList.join());
      }
      params.append(
        "dueDateFrom",
        moment(filter.dueDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "dueDateTo",
        moment(filter.dueDate.to).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append("orderBy", filter.orderBy);
      // GET TASK LIST CSV ASYNC
      const { data } = await Vue.axios.post("cases/task/list/csv", params, {
        responseType: "blob",
      });
      //
      return data;
    },
    async getTasksExcel({ state, commit }) {
      // SET FORM DATA
      const filter: CaseTaskFilter = state.tasks.filter;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      params.append("caseId", filter.caseId);
      params.append("id", filter.id);
      params.append("patientId", filter.patient.id);
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append("patientGender", filter.patient.gender);
      params.append("patientCity", filter.patient.city);
      params.append(
        "patientBirthdateFrom",
        moment(filter.patient.birthdate.from).format("YYYY-MM-DD")
      );
      params.append(
        "patientBirthdateTo",
        moment(filter.patient.birthdate.to).format("YYYY-MM-DD")
      );
      if (filter.caseTypeList && filter.caseTypeList.length) {
        params.append("caseTypeList", filter.caseTypeList.join());
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      if (filter.priorityList && filter.priorityList.length) {
        params.append(
          "priorityList",
          filter.priorityList
            .map((priority) => {
              return "'" + priority + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append("ownerUserList", filter.ownerUserList.join());
      }
      params.append(
        "dueDateFrom",
        moment(filter.dueDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "dueDateTo",
        moment(filter.dueDate.to).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      // GET TASK LIST CSV ASYNC
      const { data } = await Vue.axios.post("cases/task/list/excel", params, {
        responseType: "blob",
      });
      //
      return data;
    },
    async getTaskCalendar({ rootState }, { dueDateFrom, dueDateTo }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("dueDateFrom", dueDateFrom);
      params.append("dueDateTo", dueDateTo);
      // GET TASK CALENDAR ASYNC
      const { data } = await Vue.axios.post("cases/task/calendar", params);
      // FORMAT DATA
      const div = document.createElement("DIV");
      const events = data.map((item) => {
        // REMOVE HTML FROM DESCRIPTION (IF ANY)
        div.innerHTML = item.description;
        item.description = div.textContent || div.innerText || "";
        // SET EVENT
        item.title =
          `<div>${item.patientName}</div>` +
          `<div class='border-bottom pb-1 mb-1'>Case #${item.caseId}</div>` +
          item.description
        item.start = moment(item.dueDate).format()
        //
        return item;
      });
      //
      return events;
    },
    async getRecord({ state, commit }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      // GET CASE RECORD ASYNC
      const { data } = await Vue.axios.post("cases/record", params);
      //
      return data;
    },
    async getRecordForPDF({ state, commit }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      // GET CASE RECORD FOR PDF ASYNC
      const { data } = await Vue.axios.post("cases/record/pdf", params);
      //
      return data;
    },
    async getTypes({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/type/list", params);
      //
      commit("SET_TYPES", data);
    },
    async getTriggers({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/trigger/list", params);
      //
      commit("SET_TRIGGERS", data);
    },
    async getReferralSources({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/referral/source/list", params);
      //
      commit("SET_REF_SOURCES", data);
    },
    async getCloseReasons({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/close/reason/list", params);
      //
      commit("SET_CLOSE_REASONS", data);
    },
    async getActivity({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      // GET ACTIVITY ASYNC
      const { data } = await Vue.axios.post("cases/activity/list", params);
      //
      commit("SET_ACTIVITY", data);
    },
    async getRiskLevels({ commit }) {
      // GET RISK LEVELS ASYNC
      const { data } = await Vue.axios.post("cases/risklevel/list");
      //
      commit("SET_RISK_LEVELS", data);
    },
    async getStatus({ commit }) {
      // GET STATUS ASYNC
      const { data } = await Vue.axios.post("cases/status/list");
      //
      commit("SET_STATUS", data);
    },
    async getUsers({ rootState, commit }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET USERS ASYNC
      const { data } = await Vue.axios.post("entity/user/list", params);
      //
      commit("SET_USERS", data);
    },
    async getForms({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/form/list", params);
      //
      commit("SET_FORMS", data);
    },
    async getFormQuestions({ commit }, { caseId, formId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("caseId", caseId);
      params.append("formId", formId);
      // GET FORM QUESTIONS ASYNC
      const { data } = await Vue.axios.post("cases/form/question/list", params);
      //
      return prepareFormQuestions(data);
    },
    async getFormsExcel({ state }) {
      // SET FORM DATA
      const filter = state.dashboard.filter as CaseFilter;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      params.append("campaignId", filter.campaignId);
      params.append("id", filter.id);
      params.append("patientId", filter.patient.id);
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append("patientGender", filter.patient.gender);
      params.append("patientCity", filter.patient.city);
      params.append(
        "patientBirthdateFrom",
        moment(filter.patient.birthdate.from).format("YYYY-MM-DD")
      );
      params.append(
        "patientBirthdateTo",
        moment(filter.patient.birthdate.to).format("YYYY-MM-DD")
      );
      params.append(
        "referralDateFrom",
        moment(filter.referralDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referralDateTo",
        moment(filter.referralDate.to).format("YYYY-MM-DD")
      );
      if (filter.typeList && filter.typeList.length) {
        params.append("typeList", filter.typeList.join());
      }
      if (filter.riskLevelList && filter.riskLevelList.length) {
        params.append(
          "riskLevelList",
          filter.riskLevelList
            .map((level) => {
              return "'" + level + "'";
            })
            .join()
        );
      }
      if (filter.activityList && filter.activityList.length) {
        params.append("activityList", filter.activityList.join());
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append("ownerUserList", filter.ownerUserList.join());
      }
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateFrom",
        moment(filter.updated.from).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateTo",
        moment(filter.updated.to).format("YYYY-MM-DD")
      );
      // GET FORMS EXCEL ASYNC
      const { data } = await Vue.axios.post("cases/form/excel", params, {
        responseType: "blob",
      });
      //
      return data;
    },
    async getClinicalIndicators({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/clinicalindicator/list", params);
      //
      commit("SET_CLINICAL_INDICATORS", data);
    },
    async getCarePlanTypes({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/careplan/type/list", params);
      //
      commit("SET_CARE_PLAN_TYPES", data);
    },
    async getCarePlanProblems({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/careplan/problem/list", params);
      //
      commit("SET_CARE_PLAN_PROBLEMS", data);
    },
    async getCarePlanProblemStatus({ commit }) {
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/careplan/problem/status/list");
      //
      commit("SET_CARE_PLAN_PROBLEM_STATUS", data);
    },
    async getCarePlanInterventions({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/careplan/intervention/list", params);
      //
      commit("SET_CARE_PLAN_INTERVENTIONS", data);
    },
    async getCarePlanInterventionStatus({ commit }) {
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/careplan/intervention/status/list");
      //
      commit("SET_CARE_PLAN_INTERVENTION_STATUS", data);
    },
    async getCarePlanGoals({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/careplan/goal/list", params);
      //
      commit("SET_CARE_PLAN_GOALS", data);
    },
    async getCarePlanGoalStatus({ commit }) {
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/careplan/goal/status/list");
      //
      commit("SET_CARE_PLAN_GOAL_STATUS", data);
    },
    async getCarePlanTasks({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/careplan/task/list", params);
      //
      commit("SET_CARE_PLAN_TASKS", data);
    },
    async getCarePlanTaskStatus({ commit }) {
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/careplan/task/status/list");
      //
      commit("SET_CARE_PLAN_TASK_STATUS", data);
    },
    async getCarePlanTaskPriority({ commit }) {
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/careplan/task/priority/list");
      //
      commit("SET_CARE_PLAN_TASK_PRIORITY", data);
    },
    async getCarePlanTypeProblems({ state }, planTypeId: string) {
      // SET FORM DATA
      const { filter } = state.dashboard;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      params.append("planTypeId", planTypeId);
      // GET PROBLEMS ASYNC
      const { data } = await Vue.axios.post("cases/careplan/problem/list", params);
      //
      return data;
    },
    async getCarePlan({ commit }, { planTypeId, problemList }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("planTypeId", planTypeId);
      params.append("problemList", problemList.join());
      // GET CASE TYPES ASYNC
      const { data } = await Vue.axios.post("cases/careplan", params);
      //
      return data;
    },
    async getCarePlanExcel({ state }) {
      // SET FORM DATA
      const filter = state.dashboard.filter as CaseFilter;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      params.append("campaignId", filter.campaignId);
      params.append("id", filter.id);
      params.append("patientId", filter.patient.id);
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append("patientGender", filter.patient.gender);
      params.append("patientCity", filter.patient.city);
      params.append(
        "patientBirthdateFrom",
        moment(filter.patient.birthdate.from).format("YYYY-MM-DD")
      );
      params.append(
        "patientBirthdateTo",
        moment(filter.patient.birthdate.to).format("YYYY-MM-DD")
      );
      params.append(
        "referralDateFrom",
        moment(filter.referralDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referralDateTo",
        moment(filter.referralDate.to).format("YYYY-MM-DD")
      );
      if (filter.typeList && filter.typeList.length) {
        params.append("typeList", filter.typeList.join());
      }
      if (filter.riskLevelList && filter.riskLevelList.length) {
        params.append(
          "riskLevelList",
          filter.riskLevelList
            .map((level) => {
              return "'" + level + "'";
            })
            .join()
        );
      }
      if (filter.activityList && filter.activityList.length) {
        params.append("activityList", filter.activityList.join());
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append("ownerUserList", filter.ownerUserList.join());
      }
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateFrom",
        moment(filter.updated.from).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateTo",
        moment(filter.updated.to).format("YYYY-MM-DD")
      );
      // GET CARE PLAN EXCEL ASYNC
      const { data } = await Vue.axios.post("cases/careplan/excel", params, {
        responseType: "blob",
      });
      //
      return data;
    },
    async getLog({ commit }, { caseId, typeId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("caseId", caseId);
      params.append("typeId", typeId ? typeId : "");
      // SET CASE LOG ASYNC
      const { data } = await Vue.axios.post("cases/log/list", params);
      //
      return data;
    },
    async save({ commit }, record: CaseRecord) {
      // SET FORM DATA
      let params = new FormData();
      params.append("entityId", record.entityId);
      params.append("campaignId", record.campaignId);
      params.append("id", record.id);
      params.append("typeId", record.typeId);
      params.append("triggerId", record.triggerId);
      params.append("referralSourceId", record.referralSourceId);
      params.append("referralDate", record.referralDate);
      params.append("patientId", record.patientId);
      params.append("riskLevelId", record.riskLevelId);
      params.append("activityId", record.activityId);
      params.append("medication", record.medication);
      params.append("lang", record.lang);
      params.append("statusId", record.statusId);
      params.append("closeReasonId", record.closeReasonId);
      params.append("ownerUserId", record.ownerUserId);
      //
      const { problems, interventions, goals, tasks, forms, files, clinicalIndicators } = record;
      if (problems.length)
        params.append("problems", JSON.stringify(problems));
      if (interventions.length)
        params.append("interventions", JSON.stringify(interventions));
      if (goals.length)
        params.append("goals", JSON.stringify(goals));
      if (tasks.length)
        params.append("tasks", JSON.stringify(tasks));
      if (forms.length)
        params.append("forms", JSON.stringify(forms));
      if (files.length)
        params.append("files", JSON.stringify(files));
      if (clinicalIndicators.length)
        params.append("clinicalIndicators", JSON.stringify(clinicalIndicators));
      // SAVE CASE ASYNC
      const { data } = await Vue.axios.post("cases/save", params);
      const { caseId } = data;
      //
      return caseId;
    },
    async saveTask({ commit }, task: CaseTaskRecord) {
      // SET FORM DATA
      const params = new FormData();
      params.append("caseId", task.caseId);
      params.append("key", task.key);
      params.append("carePlanTaskId", task.carePlanTaskId);
      params.append("description", task.description);
      params.append("statusId", task.statusId);
      params.append("priorityId", task.priorityId);
      params.append("dueDate", task.dueDate);
      params.append("ownerUserId", task.ownerUserId);
      // SAVE CASE TASK ASYNC
      const { data } = await Vue.axios.post("cases/task/save", params);
      //
      return data;
    },
    async saveLog({ commit }, entry: CaseLogEntry) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", entry.id ? entry.id : "");
      params.append("caseId", entry.caseId);
      params.append("taskId", entry.taskId!);
      params.append("typeId", entry.typeId);
      params.append("comment", entry.comment ? entry.comment : "");
      // SAVE CASE LOG ASYNC
      const { data } = await Vue.axios.post("cases/log/save", params);
      //
      return data;
    },
    async saveFormAnswers({ commit }, { caseId, formId, answers }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("caseId", caseId);
      params.append("formId", formId);
      params.append("list", JSON.stringify(answers));
      // SAVE ANSWERS ASYNC
      const { data } = await Vue.axios.post("cases/form/answer/save/list", params);
      //
      return data;
    },
    async archiveTask({ commit }, { caseId, id }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("caseId", caseId);
      params.append("id", id);
      // ARCHIVE ASYNC
      const { data } = await Vue.axios.post("cases/task/archive", params);
      //
      return data;
    },
    async archiveFile({ commit }, { caseId, id }) {
      // SAVE FILE DESC TO DB
      const params = new FormData();
      params.append("caseId", caseId);
      params.append("id", id);
      const { data } = await Vue.axios.post("cases/file/archive", params);
      //
      return data;
    },
    // USER FILTERS
    async getCaseFilters({ rootState }) {
      const user: User = rootState.auth.user;
      const ownerFilter = getDefaultCaseFilter();
      ownerFilter.ownerUserList = [user.id];
      const filters = [
        {
          id: -1,
          name: "No Filter",
          json: JSON.stringify(getDefaultCaseFilter()),
        },
        {
          id: 0,
          name: "My Cases",
          json: JSON.stringify(ownerFilter),
        },
      ];
      // GET USER FILTERS ASYNC
      const { data } = await Vue.axios.post("cases/user/filter/list");
      filters.push(...data);
      //
      return filters;
    },
    async getCaseTaskFilters({ rootState }) {
      const user: User = rootState.auth.user;
      const ownerFilter = getDefaultCaseTaskFilter();
      ownerFilter.ownerUserList = [user.id];
      const filters = [
        {
          id: -1,
          name: "No Filter",
          json: JSON.stringify(getDefaultCaseTaskFilter()),
        },
        {
          id: 0,
          name: "My Tasks",
          json: JSON.stringify(ownerFilter),
        },
      ];
      // GET USER FILTERS ASYNC
      const { data } = await Vue.axios.post("cases/task/user/filter/list");
      filters.push(...data);
      //
      return filters;
    },
    async saveCaseFilter({ state }, name: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("name", name);
      params.append("json", JSON.stringify(state.dashboard.filter));
      // SAVE FILTER ASYNC
      const { data } = await Vue.axios.post("cases/user/filter/save", params);
      //
      return data;
    },
    async saveCaseTaskFilter({ state }, name: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("name", name);
      params.append("json", JSON.stringify(state.tasks.filter));
      // SAVE FILTER ASYNC
      const { data } = await Vue.axios.post("cases/task/user/filter/save", params);
      //
      return data;
    },
    async archiveCaseFilter(context, filterId: number) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("filterId", String(filterId));
      // ARCHIVE FILTER ASYNC
      const { data } = await Vue.axios.post("cases/user/filter/archive", params);
      //
      return data;
    },
    async archiveCaseTaskFilter(context, filterId: number) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("filterId", String(filterId));
      // ARCHIVE FILTER ASYNC
      const { data } = await Vue.axios.post("cases/task/user/filter/archive", params);
      //
      return data;
    },
    //
  }
};
